import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  neighborhoodProfileService,
  NeighborhoodProfile,
} from '../services/neighborhoodProfileService';
import AppSidebar from '../components/AppSidebar';
import WtiLoader from '../components/WtiLoader';
import api from '../services/api';
import { Circle, MapContainer, Marker, TileLayer } from 'react-leaflet';
import L, { LatLngExpression } from 'leaflet';

type Step = 'selectProfile' | 'investmentDetails' | 'results';

const US_STATES: { code: string; name: string }[] = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

function getHeatColor(score: number) {
  const t = Math.max(0, Math.min(1, score / 100));
  // Blend from light peach (#fee2e2) to stronger red (#fb7185)
  const start = [254, 226, 226];
  const end = [251, 113, 133];
  const r = Math.round(start[0] + (end[0] - start[0]) * t);
  const g = Math.round(start[1] + (end[1] - start[1]) * t);
  const b = Math.round(start[2] + (end[2] - start[2]) * t);
  return `rgb(${r},${g},${b})`;
}

function getHeatRadius(score: number) {
  const t = Math.max(0, Math.min(1, score / 100));
  // 6-18km radius range so the layer looks like a heat surface
  return 6000 + t * 12000;
}

const neighborhoodMarkerIcon = L.icon({
  iconUrl: '/images/marker.svg',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

const STATE_METROS: Record<string, string[]> = {
  TX: ['Dallas-Fort Worth-Arlington, TX MSA', 'Houston-The Woodlands-Sugar Land, TX MSA', 'San Antonio-New Braunfels, TX MSA', 'Austin-Round Rock-Georgetown, TX MSA'],
  FL: ['Miami-Fort Lauderdale-Pompano Beach, FL MSA', 'Orlando-Kissimmee-Sanford, FL MSA', 'Tampa-St. Petersburg-Clearwater, FL MSA', 'Jacksonville, FL MSA'],
  GA: ['Atlanta-Sandy Springs-Alpharetta, GA MSA', 'Augusta-Richmond County, GA-SC MSA', 'Savannah, GA MSA'],
  AZ: ['Phoenix-Mesa-Chandler, AZ MSA', 'Tucson, AZ MSA'],
};

const FindNeighborhood: React.FC = () => {
  const [step, setStep] = useState<Step>('selectProfile');
  const [profiles, setProfiles] = useState<NeighborhoodProfile[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'createdAt'>('name');
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [recommendations, setRecommendations] = useState<{
    geojson: any;
    items: Array<{
      id: string;
      name: string;
      state: string;
      metro: string;
      score: number;
      summary: string;
      center: [number, number];
    }>;
  } | null>(null);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<NeighborhoodProfile>({
    name: '',
    riskLabel: 'Adventurous',
    capital: 0,
    financingType: 'bank_broker',
    outcome: 'cashflow',
    propertyType: 'single_family',
    preferredState: '',
    preferredMetro: '',
    minDealPower: 0,
    minYield: 0,
  });

  const navigate = useNavigate();

  const stepClass = (target: Step) =>
    `find-step${step === target ? ' find-step--active' : ''}`;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await neighborhoodProfileService.list();
        setProfiles(data.items);
      } catch {
        toast.error('Failed to load profiles');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updateField = (field: keyof NeighborhoodProfile, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    if (step === 'selectProfile') {
      if (selectedProfileId) {
        const existing = profiles.find((p) => p._id === selectedProfileId);
        if (existing) {
          setForm({
            ...existing,
            minDealPower: existing.minDealPower ?? 0,
            minYield: existing.minYield ?? 0,
          });
        }
      }
      setStep('investmentDetails');
      return;
    }

    if (step === 'investmentDetails') {
      try {
        setLoading(true);
        const saved = await neighborhoodProfileService.create(form);
        toast.success('Profile saved');
        setProfiles((prev) => [saved, ...prev]);
        setSelectedProfileId(saved._id || null);
        setActiveProfileId(saved._id || null);
        setStep('results');
      } catch (err) {
        console.error(err);
        toast.error('Failed to save profile');
      } finally {
        setLoading(false);
      }
    }
  };

  const sortedProfiles = React.useMemo(() => {
    // Sort first
    const copy = [...profiles];
    if (sortBy === 'name') {
      copy.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else {
      copy.sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return db - da;
      });
    }

    // Then de‑duplicate by name + risk label, keeping the most recent entry
    const seen = new Set<string>();
    const unique: NeighborhoodProfile[] = [];
    for (const p of copy) {
      const key = `${p.name || ''}|${p.riskLabel || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(p);
    }

    return unique;
  }, [profiles, sortBy]);

  useEffect(() => {
    if (step !== 'results' || !activeProfileId) return;
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/neighborhood-recommendations/${activeProfileId}`);
        setRecommendations(res.data);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load neighborhood results');
      } finally {
        setLoading(false);
      }
    })();
  }, [step, activeProfileId]);

  return (
    <div className="wti-shell">
      <AppSidebar />

      <div className="wti-main">
        <div className="container dashboard-page">
          {step === 'selectProfile' && (
            <section className="card find-card">
              <header className="find-header">
                <div>
                  <h1 className="find-title">Find Your Neighborhood</h1>
                  <p className="find-subtitle">
                    Based on your risk profile, WTI&apos;s proprietary algorithm recommends
                    neighborhoods matching your risk tolerance. Select a risk profile.
                  </p>
                </div>
              </header>

              <div className="find-stepper">
                <button className={stepClass('selectProfile')}>
                  <span className="find-step-number">1</span>
                  <span>Select Risk Profile</span>
                </button>
                <button className={stepClass('investmentDetails')}>
                  <span className="find-step-number">2</span>
                  <span>Provide Investment Details</span>
                </button>
                <button className={stepClass('results')}>
                  <span className="find-step-number">3</span>
                  <span>Find Your Neighborhood</span>
                </button>
              </div>

              <div className="find-toolbar">
                <div className="find-sort">
                  <label>
                    Sort By Name
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'name' | 'createdAt')}
                    >
                      <option value="name">Name</option>
                      <option value="createdAt">Created Date</option>
                    </select>
                  </label>
                </div>
              </div>

              <div className="find-profile-list">
                {loading && <WtiLoader size="sm" message="Loading profiles…" />}
                {!loading && profiles.length === 0 && (
                  <p className="find-empty">
                    No saved profiles yet. Continue to create your first profile.
                  </p>
                )}

                {sortedProfiles.map((p) => (
                  <label
                    key={p._id}
                    className={`find-profile-row ${
                      selectedProfileId === p._id ? 'find-profile-row--selected' : ''
                    }`}
                  >
                    <div className="find-profile-main">
                      <input
                        type="radio"
                        name="selectedProfile"
                        checked={selectedProfileId === p._id}
                        onChange={() => setSelectedProfileId(p._id!)}
                      />
                      <div className="find-profile-text">
                        <div className="find-profile-name">{p.name}</div>
                        <div className="find-profile-date">
                          {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}
                        </div>
                      </div>
                    </div>
                    <span
                      className={`find-risk-pill find-risk-pill--${p.riskLabel.toLowerCase()}`}
                    >
                      {p.riskLabel}
                    </span>
                  </label>
                ))}
              </div>

              <div className="find-footer">
                <button type="button" className="btn btn-primary" onClick={handleNext}>
                  Fill Details
                </button>
              </div>
            </section>
          )}

          {step === 'investmentDetails' && (
            <section className="card find-card">
              <header className="find-header">
                <h1 className="find-title">Find Your Neighborhood</h1>
                <p className="find-subtitle">
                  Based on your risk profile, WTI&apos;s proprietary algorithm recommends
                  neighborhoods matching your risk tolerance. Provide information about the kind
                  of investment.
                </p>
              </header>

              <div className="find-stepper">
                <button className={stepClass('selectProfile')}>
                  <span className="find-step-number">1</span>
                  <span>Select Risk Profile</span>
                </button>
                <button className={stepClass('investmentDetails')}>
                  <span className="find-step-number">2</span>
                  <span>Provide Investment Details</span>
                </button>
                <button className={stepClass('results')}>
                  <span className="find-step-number">3</span>
                  <span>Find Your Neighborhood</span>
                </button>
              </div>

              <div className="find-investment">
                <h2 className="find-investment-title">Provide Investment Details</h2>
                <p className="find-investment-subtitle">
                  Provide information about the kind of investment.
                </p>

                {/* Profile name */}
                <div className="form-group">
                  <label className="find-label">Profile name*</label>
                  <input
                    type="text"
                    className="find-input"
                    value={form.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g. Dallas BRRRR profile"
                  />
                </div>

                {/* Capital with slider */}
                <div className="form-group">
                  <label className="find-label">
                    How much capital (in USD) would you like to invest?*
                  </label>
                  <input
                    type="number"
                    className="find-input"
                    value={form.capital}
                    onChange={(e) => updateField('capital', Number(e.target.value))}
                  />
                  <input
                    type="range"
                    min={25000}
                    max={1000000}
                    step={5000}
                    value={Math.max(25000, Math.min(1000000, form.capital || 0))}
                    onChange={(e) => updateField('capital', Number(e.target.value))}
                    className="find-slider"
                  />
                  <div className="find-slider-labels">
                    <span>25K</span>
                    <span>1M</span>
                  </div>
                </div>

                {/* Financing */}
                <div className="form-group">
                  <label className="find-label">What kind of financing will you get?*</label>
                  <div className="find-radio-row">
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.financingType === 'bank_broker'}
                        onChange={() => updateField('financingType', 'bank_broker')}
                      />
                      <span>Bank or Broker</span>
                    </label>
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.financingType === 'hard_money'}
                        onChange={() => updateField('financingType', 'hard_money')}
                      />
                      <span>Hard Money</span>
                    </label>
                  </div>
                </div>

                {/* Outcome */}
                <div className="form-group">
                  <label className="find-label">What outcome are you interested in?*</label>
                  <div className="find-radio-row">
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.outcome === 'cashflow'}
                        onChange={() => updateField('outcome', 'cashflow')}
                      />
                      <span>Cashflow</span>
                    </label>
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.outcome === 'equity'}
                        onChange={() => updateField('outcome', 'equity')}
                      />
                      <span>Equity</span>
                    </label>
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.outcome === 'brrrr'}
                        onChange={() => updateField('outcome', 'brrrr')}
                      />
                      <span>BRRRR (Buy,Rehab,Rent,Refinance,Repeat)</span>
                    </label>
                  </div>
                </div>

                {/* Property type */}
                <div className="form-group">
                  <label className="find-label">
                    What kind of property are you interested in?*
                  </label>
                  <div className="find-radio-row">
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.propertyType === 'single_family'}
                        onChange={() => updateField('propertyType', 'single_family')}
                      />
                      <span>Single-Family Residence</span>
                    </label>
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.propertyType === 'duplex'}
                        onChange={() => updateField('propertyType', 'duplex')}
                      />
                      <span>Duplex</span>
                    </label>
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.propertyType === 'triplex'}
                        onChange={() => updateField('propertyType', 'triplex')}
                      />
                      <span>Triplex</span>
                    </label>
                    <label className="find-radio-pill">
                      <input
                        type="radio"
                        checked={form.propertyType === 'multifamily'}
                        onChange={() => updateField('propertyType', 'multifamily')}
                      />
                      <span>Multi-Family (5+)</span>
                    </label>
                  </div>
                </div>

                {/* State dropdown (styled select) */}
                <div className="form-group">
                  <label className="find-label">
                    Is there a state you would like to invest in?
                  </label>
                  <select
                    className="find-input"
                    value={form.preferredState}
                    onChange={(e) => {
                      const code = e.target.value;
                      updateField('preferredState', code);
                      // Reset metro when state changes
                      updateField('preferredMetro', '');
                    }}
                  >
                    <option value="">Select state</option>
                    {US_STATES.filter((s) => STATE_METROS[s.code]?.length)
                      .map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Metro dropdown */}
                <div className="form-group">
                  <label className="find-label">
                    Is there a metro area you would like to invest in?
                  </label>
                  <select
                    className="find-input"
                    value={form.preferredMetro}
                    onChange={(e) => updateField('preferredMetro', e.target.value)}
                    disabled={!form.preferredState}
                  >
                    <option value="">Select metro area</option>
                    {(STATE_METROS[form.preferredState || ''] || []).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="find-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep('selectProfile')}
                >
                  Back to Select Risk Profile
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNext}
                  disabled={loading}
                >
                  {loading ? 'Saving…' : 'Find Your Neighborhood'}
                </button>
              </div>
            </section>
          )}

          {step === 'results' && (
            <section className="card find-card">
              <header className="find-header">
                <h1 className="find-title">Find Your Neighborhood</h1>
                <p className="find-subtitle">
                  Based on your profile, these neighborhoods best match your investment criteria.
                </p>
              </header>

              <div className="find-stepper">
                <button className={stepClass('selectProfile')}>
                  <span className="find-step-number">1</span>
                  <span>Select Risk Profile</span>
                </button>
                <button className={stepClass('investmentDetails')}>
                  <span className="find-step-number">2</span>
                  <span>Provide Investment Details</span>
                </button>
                <button className={stepClass('results')}>
                  <span className="find-step-number">3</span>
                  <span>Find Your Neighborhood</span>
                </button>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
                  gap: 24,
                  alignItems: 'stretch',
                }}
              >
                <div style={{ minHeight: 360 }}>
                  {recommendations?.items?.length ? (
                    <MapContainer
                      center={
                        (recommendations.items[0]?.center as LatLngExpression) || [37.8, -96]
                      }
                      zoom={4}
                      style={{ width: '100%', height: 420, borderRadius: 8 }}
                      scrollWheelZoom
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {recommendations.items.map((n) => {
                        const heatColor = getHeatColor(n.score);
                        const radius = getHeatRadius(n.score);
                        return (
                          <React.Fragment key={`heat-${n.id}`}>
                            {/* Stacked circles create a smooth heatmap-like glow */}
                            <Circle
                              center={n.center as LatLngExpression}
                              radius={radius}
                              pathOptions={{
                                color: heatColor,
                                weight: 0,
                                fillColor: heatColor,
                                fillOpacity: 0.14,
                              }}
                            />
                            <Circle
                              center={n.center as LatLngExpression}
                              radius={radius * 0.65}
                              pathOptions={{
                                color: heatColor,
                                weight: 0,
                                fillColor: heatColor,
                                fillOpacity: 0.2,
                              }}
                            />
                            <Circle
                              center={n.center as LatLngExpression}
                              radius={radius * 0.35}
                              pathOptions={{
                                color: heatColor,
                                weight: 0,
                                fillColor: heatColor,
                                fillOpacity: 0.26,
                              }}
                            />
                          </React.Fragment>
                        );
                      })}
                      {recommendations.items.map((n) => (
                        <Marker
                          key={n.id}
                          position={n.center as LatLngExpression}
                          icon={neighborhoodMarkerIcon}
                        />
                      ))}
                    </MapContainer>
                  ) : (
                    loading ? (
                      <WtiLoader size="sm" message="Loading neighborhood recommendations…" />
                    ) : (
                      <p className="find-empty">No results yet.</p>
                    )
                  )}
                </div>

                <div className="find-profile-list" style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {recommendations?.items?.map((n) => (
                    <div key={n.id} className="find-profile-row">
                      <div className="find-profile-main">
                        <div className="find-profile-text">
                          <div className="find-profile-name">{n.name}</div>
                          <div className="find-profile-date">
                            {n.metro} · {n.state}
                          </div>
                          <div style={{ fontSize: 12, color: '#4b5563', marginTop: 4 }}>
                            {n.summary}
                          </div>
                        </div>
                      </div>
                      <span className="find-risk-pill find-risk-pill--neutral">
                        Score {Math.round(n.score)}
                      </span>
                    </div>
                  ))}
                  {!loading && (!recommendations || recommendations.items.length === 0) && (
                    <p className="find-empty">No matching neighborhoods found.</p>
                  )}
                </div>
              </div>

              <div className="find-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setStep('selectProfile')}
                >
                  Back to Select Risk Profile
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to dashboard
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default FindNeighborhood;

